import React, { useState } from 'react';
import { Modal, Button, Row, Col, Image, Collapse, ListGroup, Form } from 'react-bootstrap';
import { getContributionMediaUrl, isContributionVideoType, downloadMediaUrl } from '../../helpers/images';
import { apiCall } from '../../helpers/api';

const ReviewPanel = ({
    show, onHide, submissions, collaborator, campaignId, bidId, onReview, providers,
    localReviews, onReviewToggle,
    reviewNotes, onReviewNoteChange,
    selectedVersion, onSelectedVersionChange,
    expanded, onExpandToggle,contentReviewEnabled
}) => {
    const [carouselState, setCarouselState] = useState({ show: false, idx: 0, images: [] });
    const [finalMessage, setFinalMessage] = useState("");
    const [completionLoading, setCompletionLoading] = useState(false);
    const [completionError, setCompletionError] = useState(null);
    

    const toggleExpand = (id) => {
        onExpandToggle(id);
    };

    const getLinkUrl = (linkObj) => {
        if (!linkObj) return '';
        if (typeof linkObj === 'string') return linkObj;
        return linkObj.url || linkObj.id || '';
    };

    const resolveVersionMediaUrl = (item, version, asVideo = false, downloadable = false) => {
        const mediaInput = {
            ...item,
            assetUrl: version
        };
        const args = downloadable ? null : ["w_800", "c_scale"];
        return getContributionMediaUrl(providers, mediaInput, args, downloadable, asVideo);
    };

    const handleLocalReview = (id, versionIdx, approved) => {
        if (!contentReviewEnabled) return; // Disable if content review is off
        const key = `${id}_${versionIdx}`;
        onReviewToggle(key, approved);
    };


    // Check if all submissions are approved (latest version only)
    const allApproved = submissions && submissions.length > 0 && submissions.every(item => {
        const historyVersions = Array.isArray(item.versions) ? item.versions : [];
        const normalizedCurrentVersion = item.assetUrl
            ? (typeof item.assetUrl === 'object' ? { ...item.assetUrl, name: item.assetUrl.name || item.title || item.id || 'Image' } : { url: item.assetUrl, name: item.title || item.id || 'Image' })
            : null;
        const allVersions = [...historyVersions, normalizedCurrentVersion].filter(v => v && typeof v === 'object');
        // If there are no versions at all, this item cannot be considered approved
        if (allVersions.length === 0) {
            return false;
        }
        const latest = allVersions[allVersions.length - 1];
        // Accept either meta.approved (from backend) or localReviews state
        const id = item.id;
        const idx = allVersions.length - 1;
        const localKey = `${id}_${idx}`;
        return (latest && latest.meta && latest.meta.reviewed && latest.meta.approved) || (localReviews[localKey] && localReviews[localKey].approved);
    });

    // Refactored handleCompletion to support readyForCompletion
    const handleCompletion = async (complete = true, readyForCompletion = false) => {
        setCompletionLoading(true);
        setCompletionError(null);
        try {
            let reviewPayloads = [];

            // Always run review logic before readyForCompletion
            if (!contentReviewEnabled && submissions && submissions.length > 0) {
                reviewPayloads = submissions.flatMap(item => {
                    const historyVersions = Array.isArray(item.versions) ? item.versions : [];
                    const normalizedCurrentVersion = item.assetUrl
                        ? (typeof item.assetUrl === 'object'
                            ? { ...item.assetUrl, name: item.assetUrl.name || item.title || item.id || 'Image' }
                            : { url: item.assetUrl, name: item.title || item.id || 'Image' })
                        : null;
                    const allVersions = [...historyVersions, normalizedCurrentVersion].filter(v => v && typeof v === 'object');
                    const currentVersionIdx = allVersions.length - 1;
                    if (currentVersionIdx < 0) return [];
                    return [{
                        id: item.id,
                        versionIndex: currentVersionIdx,
                        approved: complete, // true if completing, false if marking incomplete
                        reviewNotes: ''
                    }];
                });

                for (const payload of reviewPayloads) {
                    const response = await apiCall(`campaign/bid/contribution/review?id=${campaignId}`, {
                        method: 'PUT',
                        data: {
                            bidId,
                            id: payload.id,
                            versionIndex: payload.versionIndex,
                            approved: payload.approved,
                            reviewNotes: ''
                        }
                    });
                    if (!response.success) {
                        setCompletionError(response.failure?.error || 'Failed to save review.');
                        setCompletionLoading(false);
                        return;
                    }
                }
            } else if (localReviews && Object.keys(localReviews).length > 0) {
                reviewPayloads = Object.entries(localReviews).map(([key, value]) => {
                    const [contributionId, versionIdx] = key.split('_');
                    const notes = reviewNotes && Object.prototype.hasOwnProperty.call(reviewNotes, key)
                        ? reviewNotes[key]
                        : value.notes;
                    return {
                        id: contributionId,
                        versionIndex: parseInt(versionIdx, 10),
                        approved: value.approved,
                        reviewNotes: notes
                    };
                });
                for (const payload of reviewPayloads) {
                    const response = await apiCall(`campaign/bid/contribution/review?id=${campaignId}`, {
                        method: 'PUT',
                        data: {
                            bidId,
                            id: payload.id,
                            versionIndex: payload.versionIndex,
                            approved: payload.approved,
                            reviewNotes: payload.reviewNotes ?? ''
                        }
                    });
                    if (!response.success) {
                        setCompletionError(response.failure?.error || 'Failed to save review.');
                        setCompletionLoading(false);
                        return;
                    }
                }
            }

            // If readyForCompletion, call that endpoint
            if (readyForCompletion) {
                const response = await apiCall(`campaign/bid/readyForCompletion?id=${campaignId}`, {
                    method: "PUT",
                    data: { bidID: bidId, message: finalMessage }
                });
                if (response["success"]) {
                    setFinalMessage("");
                    if (onReview) onReview(reviewPayloads, true);
                    onHide();
                } else {
                    setCompletionError(response["failure"] && response["failure"]["error"] ? response["failure"]["error"] : "Unknown error");
                }
                setCompletionLoading(false);
                return;
            }

            // Otherwise, normal complete/incomplete
            const endpoint = complete ? `campaign/bid/complete?id=${campaignId}` : `campaign/bid/incomplete?id=${campaignId}`;
            const response = await apiCall(endpoint, {
                method: "PUT",
                data: { bidID: bidId, message: finalMessage }
            });
            if (response["success"]) {
                setFinalMessage("");
                if (onReview) {
                    onReview(reviewPayloads, true);
                }
                onHide();
            } else {
                setCompletionError(response["failure"] && response["failure"]["error"] ? response["failure"]["error"] : "Unknown error");
            }
        } catch (err) {
            setCompletionError("Failed to update completion status.");
        } finally {
            setCompletionLoading(false);
        }
    };
    
    const handleClose = () => {
        setFinalMessage("");
        setCompletionError(null);
        setCarouselState({ show: false, idx: 0, images: [] });
        onHide();
    };

    return (
        <>
            <Modal show={show} onHide={handleClose} size="lg" centered backdrop="static">
                <Modal.Header closeButton>
                    <Modal.Title>
                        Review Submissions
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {submissions && submissions.length > 0 ? (
                        <Row>
                            {submissions.map((item, idx) => {
                                const contributionId = item.id;
                                // Normalize assetUrl so that string links are treated as a version object
                                const historyVersions = Array.isArray(item.versions) ? item.versions : [];
                                const normalizedCurrentVersion = item.assetUrl
                                    ? (typeof item.assetUrl === 'object' ? { ...item.assetUrl, name: item.assetUrl.name || item.title || item.id || 'Image' } : { url: item.assetUrl, name: item.title || item.id || 'Image' })
                                    : null;
                                const allVersions = [...historyVersions, normalizedCurrentVersion].filter(v => v && typeof v === 'object');
                                const currentVersionIdx = allVersions.length - 1; 
                                if (allVersions.length === 0) return null;
                                const selectedIdx = selectedVersion[contributionId] !== undefined ? selectedVersion[contributionId] : currentVersionIdx; 
                                const selectedVer = allVersions[selectedIdx];
                                const localReviewKey = `${contributionId}_${currentVersionIdx}`;
                                const localReview = localReviews[localReviewKey];
                                let isDraft = false;
                                let backendReviewed = selectedVer && selectedVer.meta && typeof selectedVer.meta.reviewed === 'boolean' ? selectedVer.meta.reviewed : false;
                                let backendApproved = selectedVer && selectedVer.meta && typeof selectedVer.meta.approved === 'boolean' ? selectedVer.meta.approved : undefined;
                                // Draft if local review differs from backend (unsaved changes)
                                if (localReview && typeof localReview.approved === 'boolean') {
                                    if (backendReviewed && typeof backendApproved === 'boolean') {
                                        isDraft = localReview.approved !== backendApproved;
                                    } else {
                                        isDraft = true;
                                    }
                                }
                                // Show status badge ONLY for current version
                                let statusBadge = null;
                                if (contentReviewEnabled && selectedIdx === currentVersionIdx) {
                                    if (localReview && typeof localReview.approved === 'boolean' && isDraft) {
                                        // Show local review status (draft) ONLY if not saved
                                        statusBadge = (
                                            <span className={`badge ms-2 ${localReview.approved ? 'bg-success' : 'bg-danger'}`}>
                                                {localReview.approved ? 'Approved' : 'Rejected'} 
                                            </span>
                                        );
                                    } else if (localReview && typeof localReview.approved === 'boolean' && backendReviewed && typeof backendApproved === 'boolean' && localReview.approved === backendApproved) {
                                        // After save, show only Approved/Rejected if backend matches local
                                        statusBadge = (
                                            <span className={`badge ms-2 ${backendApproved ? 'bg-success' : 'bg-danger'}`}>
                                                {backendApproved ? 'Approved' : 'Rejected'}
                                            </span>
                                        );
                                    } else if (backendReviewed && typeof backendApproved === 'boolean') {
                                        // Show backend status (saved) ONLY if reviewed and matches local
                                        statusBadge = (
                                            <span className={`badge ms-2 ${backendApproved ? 'bg-success' : 'bg-danger'}`}>
                                                {backendApproved ? 'Approved' : 'Rejected'}
                                            </span>
                                        );
                                    } else {
                                        statusBadge = (
                                            <span className="badge bg-secondary ms-2">Pending Review</span>
                                        );
                                    }
                                }
                                return (
                                    <Col xs={12} md={6} key={contributionId || idx} className="mb-4">
                                        <div className="border rounded p-3 h-100 d-flex flex-column justify-content-between">
                                            <div>
                                                <div className="fw-bold mb-1">
                                                    {statusBadge}
                                                </div>
                                                {item.description && (
                                                    <div className="mb-2 text-muted small">{item.description}</div>
                                                )}
                                                {((item.assetType === 'image' || item.assetType === 'video' || isContributionVideoType(item)) && allVersions.length > 0) && (
                                                    <div
                                                        className="mb-2 position-relative"
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => setCarouselState({
                                                            show: true,
                                                            idx: selectedIdx,
                                                            images: allVersions.map(ver => ({
                                                                type: isContributionVideoType(item) ? 'video' : 'image',
                                                                url: resolveVersionMediaUrl(item, ver, isContributionVideoType(item), false),
                                                                downloadUrl: resolveVersionMediaUrl(item, ver, isContributionVideoType(item), true),
                                                                name: ver.id || item.title
                                                            }))
                                                        })}
                                                    >
                                                        {!isContributionVideoType(item) && (
                                                            <Image
                                                                src={resolveVersionMediaUrl(item, selectedVer, false, false)}
                                                                alt={item.title}
                                                                fluid
                                                                rounded
                                                                style={{ maxHeight: 300, objectFit: 'contain' }}
                                                            />
                                                        )}
                                                        {isContributionVideoType(item) && (
                                                            <video
                                                                src={resolveVersionMediaUrl(item, selectedVer, true, false)}
                                                                controls
                                                                className="w-100 rounded"
                                                                style={{ maxHeight: '300px' }}
                                                            />
                                                        )}
                                                    </div>
                                                )}
                                                {item.assetType === 'link' && (
                                                    <div className="mb-2">
                                                        {(() => {
                                                            const rawUrl = getLinkUrl(selectedVer) || getLinkUrl(item.assetUrl);
                                                            const safeUrl = (typeof rawUrl === 'string' && (rawUrl.startsWith('http://') || rawUrl.startsWith('https://'))) ? rawUrl : null;
                                                            return safeUrl ? (
                                                                <a
                                                                    href={safeUrl}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                >
                                                                    {safeUrl}
                                                                </a>
                                                            ) : (
                                                                <span className="text-danger">Invalid or unsafe link</span>
                                                            );
                                                        })()}
                                                    </div>
                                                )}
                                                {contentReviewEnabled ? (
                                                    <>
                                                        {selectedIdx === currentVersionIdx && !(selectedVer && selectedVer.meta && selectedVer.meta.reviewed && selectedVer.meta.approved) && (
                                                            <>
                                                                <Form.Group className="w-100 mb-2">
                                                                    <Form.Control
                                                                        size="sm"
                                                                        type="text"
                                                                        placeholder="Review notes (optional)"
                                                                        value={reviewNotes[localReviewKey] || ''}
                                                                        onChange={e => onReviewNoteChange(localReviewKey, e.target.value)}
                                                                    />
                                                                </Form.Group>
                                                                <div className="d-flex gap-2 mb-2">
                                                                    <Button
                                                                        variant={localReviews && localReviews[localReviewKey]?.approved === true ? "success" : "outline-success"}
                                                                        size="sm"
                                                                        onClick={() => handleLocalReview(contributionId, currentVersionIdx, true)}
                                                                    >
                                                                        {localReviews && localReviews[localReviewKey]?.approved === true ? 'Approved' : 'Approve'}
                                                                    </Button>
                                                                    <Button
                                                                        variant={localReviews && localReviews[localReviewKey]?.approved === false ? "danger" : "outline-danger"}
                                                                        size="sm"
                                                                        onClick={() => handleLocalReview(contributionId, currentVersionIdx, false)}
                                                                    >
                                                                        {localReviews && localReviews[localReviewKey]?.approved === false ? 'Rejected' : 'Reject'}
                                                                    </Button>
                                                                </div>
                                                                {localReviews[localReviewKey] && (
                                                                    <div className={`alert ${localReviews[localReviewKey].approved ? 'alert-success' : 'alert-danger'} py-2 small`}>
                                                                        <strong>{localReviews[localReviewKey].approved ? ' Approved' : ' Rejected'}</strong>
                                                                        {localReviews[localReviewKey].notes && (
                                                                            <div className="mt-1 fst-italic">{localReviews[localReviewKey].notes}</div>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </>
                                                        )}
                                                        {selectedIdx < currentVersionIdx && (
                                                            <div className="alert alert-info small">
                                                                This is an old version. Only the current version can be reviewed.
                                                            </div>
                                                        )}
                                                        {allVersions.length > 1 && (
                                                            <div className="mb-2">
                                                                <Button
                                                                    variant="outline-secondary"
                                                                    size="sm"
                                                                    onClick={() => toggleExpand(contributionId)}
                                                                    className="mb-2"
                                                                >
                                                                    {expanded[contributionId] ? 'Hide' : 'Show'} Version History
                                                                </Button>
                                                                <Collapse in={!!expanded[contributionId]}>
                                                                    <div>
                                                                        <ListGroup variant="flush">
                                                                            {allVersions.map((ver, vIdx) => (
                                                                                <ListGroup.Item
                                                                                    key={vIdx}
                                                                                    className={`d-flex flex-column align-items-start gap-2${selectedIdx === vIdx ? ' bg-light' : ''}`}
                                                                                    style={{ cursor: 'pointer' }}
                                                                                    onClick={() => onSelectedVersionChange(contributionId, vIdx)}
                                                                                >
                                                                                    <div className="d-flex align-items-center w-100">
                                                                                        <span className="me-2 small text-muted">
                                                                                            {vIdx === currentVersionIdx ? 'Current' : `v${vIdx + 1}`}
                                                                                        </span>
                                                                                        {ver?.id && ver?.source && providers && !isContributionVideoType(item) && (
                                                                                            <Image
                                                                                                src={getContributionMediaUrl(providers, { ...item, assetUrl: ver }, ["w_48", "h_48", "c_scale"], false, false)}
                                                                                                alt={`Version ${vIdx + 1}`}
                                                                                                width={48}
                                                                                                height={48}
                                                                                                rounded
                                                                                                className="me-2 border"
                                                                                            />
                                                                                        )}
                                                                                        <span className="small">
                                                                                            {ver.meta && ver.meta.reviewed 
                                                                                                ? (ver.meta.approved ? 'Approved' : 'Rejected') 
                                                                                                : 'Pending Review'}
                                                                                        </span>
                                                                                        {ver.meta && ver.meta.reviewedAt ? (
                                                                                            <span className="ms-2 small text-muted">
                                                                                                {new Date(ver.meta.reviewedAt * 1000).toLocaleDateString()}
                                                                                            </span>
                                                                                        ) : ""}
                                                                                    </div>
                                                                                    {ver.meta && ver.meta.reviewNotes && (
                                                                                        <div className="small text-muted fst-italic">
                                                                                            {ver.meta.reviewNotes}
                                                                                        </div>
                                                                                    )}
                                                                                </ListGroup.Item>
                                                                            ))}
                                                                        </ListGroup>
                                                                    </div>
                                                                </Collapse>
                                                            </div>
                                                        )}
                                                    </>
                                                ) : (
                                                    <br></br>
                                                )}
                                            </div>
                                        </div>
                                    </Col>
                                );
                            })}
                        </Row>
                    ) : (
                        <div className="text-center text-muted py-4">No submissions to review.</div>
                    )}
                    <p className="mb-3">
                        <strong>{collaborator?.profile?.name}</strong> marked this collaboration as complete.
                    </p>
                    
                    <p className="mb-3">
                        If you agree you can confirm by clicking on the "Confirm Completion" button below. 
                        However if you feel that they haven't completed the task or something else is stopping 
                        them from completing the collaboration, please click on the "Mark as Incomplete" button. If any submissions are marked as rejected, you won't be able to confirm completion until all submissions are approved. You can still mark as incomplete regardless of review status.
                    </p>
                    
                    <p className="mb-3">
                        You have an option to send one final message to the influencer as well - either to 
                        thank them or let them know what you still expect from them.
                    </p>
                    
                    <Form.Group>
                        <Form.Label>Enter a message to send to {collaborator?.profile?.name}:</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={5}
                            placeholder="Write your message here..."
                            value={finalMessage}
                            onChange={e => setFinalMessage(e.target.value)}
                            disabled={completionLoading}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <div className="d-flex align-items-center">
                        <Button
                            variant="danger"
                            size="sm"
                            className="ms-2"
                            disabled={completionLoading}
                            onClick={() => handleCompletion(false)}
                        >
                            {completionLoading ? 'Marking...' : 'Mark as Incomplete'}
                        </Button>
                        {(contentReviewEnabled ? allApproved : true) && (
                            (collaborator?.campaignType === "gifting" && collaborator?.bidState === "In Review") ? (
                                <Button
                                    variant="success"
                                    size="sm"
                                    className="ms-2"
                                    disabled={completionLoading}
                                    onClick={() => handleCompletion(true, true)}
                                >
                                    Ready for Completion
                                </Button>
                            ) : (
                                <Button
                                    variant="success"
                                    size="sm"
                                    className="ms-2"
                                    disabled={completionLoading}
                                    onClick={() => handleCompletion(true)}
                                >
                                    Confirm Completion
                                </Button>
                            )
                        )}
                    </div>
                    <div className="ms-auto d-flex gap-2">
                        <Button variant="secondary" onClick={handleClose} disabled={completionLoading}>Close</Button>
                    </div>
                    {completionError && <div className="text-danger ms-3">{completionError}</div>}
                </Modal.Footer>
            </Modal>
            {carouselState.show && (
                <div
                    role="dialog"
                    aria-modal="true"
                    tabIndex={-1}
                    onKeyDown={e => {
                        if (e.key === 'Escape') {
                            setCarouselState({ ...carouselState, show: false });
                        }
                    }}
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.95)',
                        zIndex: 10000,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                    ref={el => { if (el) el.focus(); }}
                >
                    <div style={{ position: 'absolute', top: 20, right: 20, zIndex: 10001 }}>
                        <button
                            type="button"
                            onClick={() => downloadMediaUrl(carouselState.images[carouselState.idx]?.downloadUrl || carouselState.images[carouselState.idx]?.url, carouselState.images[carouselState.idx]?.name || 'submission')}
                            className="btn btn-sm btn-success me-2"
                        >
                            Download
                        </button>
                        <button 
                            type="button" 
                            onClick={() => setCarouselState({ ...carouselState, show: false })} 
                            className="btn btn-sm btn-light"
                        >
                            Close
                        </button>
                    </div>
                    <div style={{ maxWidth: '90vw', maxHeight: '90vh' }}>
                        {carouselState.images[carouselState.idx]?.type === 'video' ? (
                            <video 
                                autoPlay 
                                loop 
                                controls 
                                className="rounded" 
                                src={carouselState.images[carouselState.idx]?.url} 
                                style={{ maxHeight: '85vh', maxWidth: '90vw', objectFit: 'contain' }}
                            />
                        ) : (
                            <img 
                                className="rounded" 
                                src={carouselState.images[carouselState.idx]?.url} 
                                alt={carouselState.images[carouselState.idx]?.name}
                                style={{ maxHeight: '85vh', maxWidth: '90vw', objectFit: 'contain' }}
                            />
                        )}
                    </div>
                </div>
            )}
        </>
    );
};

export default ReviewPanel;